import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import api from '../services/api';
import { setTenant } from '../store/slices/tenantSlice';
import { selectIsAuthenticated, selectIsSuperAdmin, selectUser } from '../store/slices/authSlice';

const SESSION_KILL_CODES = new Set([
  'TENANT_SUSPENDED',
  'TENANT_DEACTIVATED',
  'TENANT_INACTIVE',
  'DEMO_EXPIRED',
  'DEMO_LOCKED',
]);

function isTenantSessionKill(err: unknown): boolean {
  if (!(err instanceof AxiosError)) return false;
  const data = err.response?.data as { code?: string; message?: string } | undefined;
  if (data?.code && SESSION_KILL_CODES.has(data.code)) return true;
  const message = String(data?.message || '').toLowerCase();
  return message.includes('tenant is suspended')
    || message.includes('tenant is deactivated')
    || message.includes('tenant not found or inactive');
}

/** Reload current tenant modules/features into Redux (e.g. after admin toggles modules). */
export async function refreshTenantSession(dispatch: Dispatch<UnknownAction>) {
  const slug = localStorage.getItem('tenantSlug') || 'demo';
  try {
    const [tenantRes, modulesRes] = await Promise.all([
      api.get('/tenants/current'),
      api.get('/modules'),
    ]);
    dispatch(setTenant({
      id: tenantRes.data.data._id,
      companyName: tenantRes.data.data.companyName,
      slug: tenantRes.data.data.slug,
      branding: tenantRes.data.data.branding,
      modules: tenantRes.data.data.modules,
      currency: tenantRes.data.data.currency,
      currencyFormat: tenantRes.data.data.currencyFormat ?? null,
      enabledFeatures: modulesRes.data.data.enabledFeatures || [],
      isDemo: Boolean(tenantRes.data.data.isDemo),
      demoExpiresAt: tenantRes.data.data.demoExpiresAt || null,
    }));
  } catch (err) {
    // Do not invent a fake tenant when access was revoked — let the API interceptor force logout.
    if (isTenantSessionKill(err)) throw err;
    dispatch(setTenant({
      slug,
      companyName: 'Demo Super App',
      enabledFeatures: [],
      isDemo: false,
      demoExpiresAt: null,
    }));
  }
}

export function useTenantBootstrap() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const user = useSelector(selectUser);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (isSuperAdmin && !localStorage.getItem('tenantSlug')) {
      localStorage.setItem('tenantSlug', 'demo');
    }

    const load = () => { void refreshTenantSession(dispatch); };
    load();

    const onFocus = () => load();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [dispatch, isAuthenticated, isSuperAdmin, user?.tenantId]);
}
